import { useCallback, useEffect, useRef, useState } from 'react';
import { Game } from '@/game/Game';
import { GameSnapshot, NotificationSnapshot } from '@/game/Snapshot';
import { loadDevSessionState, patchDevSessionState } from '@/game/DevSessionState';
import { Hud } from '@/ui/Hud';
import { defaultHudVisibility, HudVisibility, sanitizeHudVisibility } from '@/ui/HudSections';
import { RotatePrompt } from '@/ui/RotatePrompt';
import { TouchControls } from '@/ui/TouchControls';
import { COMPACT_QUERY, useIsPortraitPhone, useIsTouch } from '@/ui/useMediaQuery';
import { HelpPanel, MapPanel, PanelId, SettingsPanel } from '@/ui/Panels';
import '@/ui/styles.css';

/**
 * How much harder a finger steers than a mouse, per pixel of travel.
 *
 * A mouse can be picked up and re-centred, so a long turn costs several
 * strokes and nobody minds. A finger has one screen-width of travel before it
 * runs out of glass, so the same gain would make the vessel feel nailed down.
 */
const TOUCH_LOOK_SCALE = 1.4;

/**
 * Application shell.
 *
 * The canvas is owned by the game; React only draws the overlay. The two
 * communicate through a snapshot published at 10 Hz, which keeps React
 * reconciliation entirely off the render loop and means a heavy panel can never
 * cost the simulation a frame.
 */
export function App(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const gameRef = useRef<Game | null>(null);
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  // Restored from DevSessionState (see that file's comment) so a shader-edit
  // reload doesn't also close whatever panel was open — the same reasoning
  // that restores the free-flight camera pose, applied to the UI instead of
  // the game state.
  const [panel, setPanel] = useState<PanelId>(() => {
    const saved = loadDevSessionState().panel;
    const known: readonly PanelId[] = ['map', 'settings', 'help'];
    return (known as readonly string[]).includes(saved ?? '') ? (saved as PanelId) : null;
  });
  const [toasts, setToasts] = useState<NotificationSnapshot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const touch = useIsTouch();
  const portrait = useIsPortraitPhone();
  // Reset every time the device returns to landscape, so the prompt is a
  // per-rotation nudge rather than a preference the player has to live with
  // after dismissing it once by accident.
  const [portraitDismissed, setPortraitDismissed] = useState(false);
  const askToRotate = touch && portrait && !portraitDismissed;
  // Which readouts are on screen. Restored from the last session if the player
  // ever chose; otherwise picked from the viewport width, because the set that
  // fits a monitor buries a phone (see HudSections.ts).
  const [hudVisibility, setHudVisibility] = useState<HudVisibility>(() => {
    const fallback = defaultHudVisibility(
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(COMPACT_QUERY).matches
        : false,
    );
    const saved = loadDevSessionState().hud;
    return saved ? sanitizeHudVisibility(saved, fallback) : fallback;
  });
  // Hides the whole HUD overlay — readouts, the panel-launcher row, toasts,
  // and toasts — so it isn't permanently eating screen space
  // on a small touch display. The toggle button itself and the pause overlay
  // are the only things exempt from this, on purpose: one has to stay
  // reachable to bring the HUD back, and the other is state feedback, not a
  // panel.
  const [hudHidden, setHudHidden] = useState(false);

  useEffect(() => {
    patchDevSessionState({ panel });
  }, [panel]);

  useEffect(() => {
    patchDevSessionState({ hud: hudVisibility });
  }, [hudVisibility]);

  useEffect(() => {
    if (!portrait) setPortraitDismissed(false);
  }, [portrait]);

  // The vessel must not keep obeying a control the prompt has just covered.
  useEffect(() => {
    if (askToRotate) gameRef.current?.input.clearVirtualInput();
  }, [askToRotate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ?quality=high forces a profile. Capability detection cannot see past a
    // software renderer, so without an override the automated visual checks can
    // only ever exercise the cheapest path — which is exactly how a bug in the
    // expensive one survives.
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('quality');
    const quality =
      requested === 'low' || requested === 'medium' || requested === 'high' || requested === 'ultra'
        ? requested
        : undefined;

    let game: Game;
    try {
      game = new Game(canvas, quality);
    } catch (err) {
      console.error('[App] failed to start the game', err);
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    gameRef.current = game;
    // Exposed for diagnostics and automated checks. Read-only in practice; the
    // UI never mutates the game through it.
    (window as unknown as { __solarFrontier?: Game }).__solarFrontier = game;

    const offSnapshot = game.events.on('snapshot', (s) => setSnapshot(s));
    const offNotification = game.events.on('notification', (n) => {
      setToasts((prev) => [...prev.slice(-3), n]);
      // Toasts expire on their own; the full log stays in the snapshot.
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== n.id));
      }, 6500);
    });

    game.start();
    // Storage is opened after the loop is already running. It can be slow or
    // unavailable, and neither is a reason to delay the first frame.
    void game.openStorage();

    return () => {
      offSnapshot();
      offNotification();
      game.dispose();
      gameRef.current = null;
    };
  }, []);

  // Panel hotkeys are handled here rather than in the game so the game never
  // needs to know the UI exists.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const map: Record<string, PanelId> = {
        KeyM: 'map',
        F9: 'settings',
        F1: null,
      };
      if (e.code === 'Escape') {
        setPanel((p) => (p ? null : p));
        return;
      }
      if (e.code === 'Slash' && e.shiftKey) {
        e.preventDefault();
        setPanel((p) => (p === 'help' ? null : 'help'));
        return;
      }
      const target = map[e.code];
      if (target === undefined) return;
      if (target === null) return;
      e.preventDefault();
      setPanel((p) => (p === target ? null : target));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Suppress flight input while a panel is open, so typing in a panel does not
  // fire the vessel's thrusters.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    game.input.uiFocus = panel !== null;
    if (panel !== null) {
      game.input.exitPointerLock();
      // The on-screen controls unmount behind the panel; anything held at that
      // moment has to be released or the vessel keeps obeying a finger that is
      // no longer on the glass.
      game.input.clearVirtualInput();
    }
  }, [panel]);

  const closePanel = useCallback(() => setPanel(null), []);

  /**
   * Viewport pointer handling.
   *
   * A mouse locks to the canvas and steers through `movementX/Y`; a finger
   * cannot lock anything, so it steers by dragging and the delta is measured
   * here. Both end up in `InputManager.applyLookDelta`, so the vessel is flown
   * by exactly one piece of code either way — a touch device does not get its
   * own control law.
   */
  const lookPointer = useRef<{ id: number; x: number; y: number } | null>(null);

  const onViewportPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (panel !== null) return;
      // Browsers only allow an AudioContext to start from a user gesture, so
      // the first press into the viewport is what brings sound up.
      gameRef.current?.audio.start();
      if (e.pointerType !== 'touch') {
        gameRef.current?.input.requestPointerLock();
        return;
      }
      if (lookPointer.current !== null) return;
      lookPointer.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [panel],
  );

  const onViewportPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const p = lookPointer.current;
    if (!p || p.id !== e.pointerId) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    gameRef.current?.input.applyLookDelta(dx * TOUCH_LOOK_SCALE, dy * TOUCH_LOOK_SCALE);
  }, []);

  const onViewportPointerEnd = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (lookPointer.current?.id === e.pointerId) lookPointer.current = null;
  }, []);

  const game = gameRef.current;

  return (
    // `touch` on the root is what lifts the bottom readouts clear of the
    // on-screen controls; the stylesheet cannot detect the same thing, since
    // `?touch=` can override the media query either way.
    <div className={`app${touch ? ' touch' : ''}`}>
      <canvas
        ref={canvasRef}
        className="viewport"
        onPointerDown={onViewportPointerDown}
        onPointerMove={onViewportPointerMove}
        onPointerUp={onViewportPointerEnd}
        onPointerCancel={onViewportPointerEnd}
        onLostPointerCapture={onViewportPointerEnd}
      />

      {error && (
        <div className="loading">
          <div className="loading-inner">
            <h1>SOLAR FRONTIER</h1>
            <p style={{ color: 'var(--bad)' }}>{error}</p>
            <p>
              This build requires WebGL2. If your browser supports it, check that hardware acceleration is enabled and
              reload.
            </p>
          </div>
        </div>
      )}

      {!error && !snapshot && (
        <div className="loading">
          <div className="loading-inner">
            <h1>SOLAR FRONTIER</h1>
            <p>
              Generating the Cyrene system: nine planets, their moons, and the terrain beneath your landing gear. All of
              it derived from one seed, so it will be identical every time you return.
            </p>
            <div className="loading-bar">
              <span />
            </div>
          </div>
        </div>
      )}

      {askToRotate && <RotatePrompt onDismiss={() => setPortraitDismissed(true)} />}

      {snapshot && game && !askToRotate && (
        <div className="overlay">
          {/* Fixed in the top-right corner so a thumb can find it without
              hunting, and never itself hidden by anything it controls — a HUD
              switched off has to stay switchable back on, and the switch lives
              in Settings.

              Stood down while a panel is open: a panel is a modal, and at the
              phone breakpoint it is a full-screen sheet whose Close button
              lands in exactly this corner. Leaving the button on top of it made
              the sheet impossible to close by tapping Close. */}
          {panel === null && (
            <div className="corner-toggle">
              <button
                type="button"
                className="launcher-toggle"
                aria-label="Settings"
                onClick={() => setPanel('settings')}
              >
                ⚙
              </button>
            </div>
          )}

          {!hudHidden && (
            <>
              <Hud snapshot={snapshot} visibility={hudVisibility} />

              <div className="notifications">
                {toasts.map((t) => (
                  <div key={t.id} className={`notification ${t.severity}`}>
                    <div className="title">{t.title}</div>
                    <div className="body">{t.body}</div>
                  </div>
                ))}
              </div>

              <div
                className="launcher-row"
                style={{
                  marginTop: toasts.length > 0 ? 8 + toasts.length * 46 : 0,
                }}
              >
                <div className="button-row">
                  {(
                    [
                      ['map', 'Map'],
                      ['settings', 'Settings'],
                      ['help', 'Keys'],
                    ] as [PanelId, string][]
                  ).map(([id, label]) => (
                    <button
                      key={label}
                      className={panel === id ? 'active' : ''}
                      onClick={() => setPanel(panel === id ? null : id)}
                    >
                      {label}
                    </button>
                  ))}
                  <button onClick={() => game.toggleOnFoot()}>{snapshot.player.onFoot ? 'Board' : 'Disembark'}</button>
                  {snapshot.tour && (
                    <button onClick={() => game.skipToStop(-1)} disabled={snapshot.tour.stopIndex === 0}>
                      ◀ Prev
                    </button>
                  )}
                  <button
                    className={snapshot.tour ? 'active' : ''}
                    onClick={() => (snapshot.tour ? game.stopTour() : game.startTour())}
                  >
                    {snapshot.tour ? 'Stop Tour' : 'Tour'}
                  </button>
                  {snapshot.tour && (
                    <button
                      onClick={() => game.skipToStop(1)}
                      disabled={snapshot.tour.stopIndex >= snapshot.tour.stopCount - 1}
                    >
                      Next ▶
                    </button>
                  )}
                </div>
                {snapshot.tour && (
                  <div style={{ textAlign: 'center', marginTop: 6, fontSize: 12, color: 'var(--text-dim)' }}>
                    Touring {snapshot.tour.bodyName} ({snapshot.tour.stopIndex + 1}/{snapshot.tour.stopCount}) —{' '}
                    {snapshot.tour.phase} {Math.round(snapshot.tour.phaseProgress * 100)}%
                  </div>
                )}
              </div>
            </>
          )}

          {/* On-screen controls: shown for a coarse pointer, and never behind
              an open panel — the panel is what the player is touching then,
              and a stick under it would take the first drag meant for a list. */}
          {touch && panel === null && <TouchControls game={game} snapshot={snapshot} />}

          {snapshot.paused && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(5, 7, 13, 0.55)',
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontSize: 22, letterSpacing: '0.3em', color: 'var(--text-bright)' }}>PAUSED</div>
            </div>
          )}

          {panel === 'map' && <MapPanel game={game} snapshot={snapshot} onClose={closePanel} />}
          {panel === 'settings' && (
            <SettingsPanel
              game={game}
              snapshot={snapshot}
              onClose={closePanel}
              visibility={hudVisibility}
              onVisibilityChange={setHudVisibility}
              hudHidden={hudHidden}
              onHudHiddenChange={setHudHidden}
            />
          )}
          {panel === 'help' && <HelpPanel onClose={closePanel} />}
        </div>
      )}
    </div>
  );
}
